import { Button } from "@aws-amplify/ui-react"
import { useContext } from "react"
import ConfigurationContext from "../context/ConfigurationContext.js"
import Translation from "../i18n/Translation.js"
import type { Branding } from "../schemata/biometry/Branding.js"
import style from "../style/Landing.module.css"
import BrandedFooter from "./BrandedFooter.js"
import BrandedHeader from "./BrandedHeader.js"
import LandingIllustration from "./LandingIllustration.js"
import LandingSteps from "./LandingSteps.js"
import PhotosensitiveWarning from "./PhotosensitiveWarning.js"

type Properties = Readonly<{ branding: Branding | undefined; isBrandingSettled: boolean; onStart: () => void }>

/**
 * @description
 *	The screen shown before the camera stage.
 *
 * @remarks
 *	There is never a scroll: the landing is split into 3 (three) zones, the
 *	top and the bottom keep their natural height and the middle one is the
 *	only one that gets clipped, so the logo and the button never move. What
 *	the middle zone shows is driven by a ladder of viewport heights, declared
 *	as custom properties in the stylesheet.
 *
 *	The explanation is held back until the branding settles, because an
 *	integration that explains the process on its own previous screen turns it
 *	off, and it would be seen appearing and disappearing otherwise. The button
 *	is held back with it, because it is painted with the accent, which is not
 *	declared until the branding settles: it would be seen without a background
 *	and with its text untinted. The logo never waits, because it is the
 *	branding itself and there is nothing to show before it.
 */
export default function Landing({ branding, isBrandingSettled, onStart }: Properties) {
	const displayText = Translation[useContext(ConfigurationContext).DEFAULT_LOCALE]
	const classNameWhilePending = (className: string): string =>
		isBrandingSettled ? className : `${className} ${style["landing-pending"]}`
	return (
		<div
			className={
				branding?.showInstructions === false
					? `${style["landing-root"]} ${style["landing-compact"]}`
					: style["landing-root"]
			}
		>
			<div className={style["landing-top"]}>
				<BrandedHeader branding={branding} />
			</div>
			<div className={style["landing-middle"]}>
				<h1 className={style["landing-title"]}>{displayText.landingTitle}</h1>
				<div className={classNameWhilePending(style["landing-instructions"])}>
					<p className={style["landing-subtitle"]}>{displayText.landingSubtitle}</p>
					<div className={style["landing-steps-slot"]}>
						<LandingSteps />
					</div>
					<div className={style["landing-illustration-slot"]}>
						<LandingIllustration />
					</div>
				</div>
			</div>
			<div className={style["landing-bottom"]}>
				<PhotosensitiveWarning
					warningHeading={displayText.photosensitiveWarningHeading!}
					warningInfo={displayText.photosensitiveWarningInfo!}
					warningText={displayText.photosensitiveWarningText!}
				/>
				<Button
					className={classNameWhilePending(style["landing-button"])}
					onClick={onStart}
					variation="primary"
				>
					{displayText.landingStartButton}
				</Button>
				<BrandedFooter />
			</div>
		</div>
	)
}
